import React, { useEffect, useRef } from 'react';
import { DragDropProvider } from '@dnd-kit/react';
import { isSortable } from '@dnd-kit/react/sortable';
import { FormGroup } from '../../../../components/FormGroup';
import { MsComboBox } from '../../../../components/MsComboBox';
import { TooltipIcon } from '../../../../components/TooltipIcon';
import { ToggleSwitch } from '../../../../components/ToggleSwitch';
import { useRelativityIioFields } from '../../../../hooks/useRelativitySettings';
import {
  useRelativityWorkspaceFields,
  useRelativityWorkspaceFolders,
  useRelativityWorkspaces,
} from '../../../../hooks/useRelativityWorkspace';
import { useProjectIdFromRoute } from '../../../../hooks/useProjectIdFromRoute';
import type { RelativityField, RelativityFolder, RelativitySettingsField, RelativitySettingsItem, RelativityWorkspace } from '../../../../types/relativitySettings';
import { FieldMappingRow } from './FieldMappingRow';

const EMPTY_WORKSPACES: RelativityWorkspace[] = [];
const EMPTY_WORKSPACE_FIELDS: RelativityField[] = [];
const EMPTY_WORKSPACE_FOLDERS: RelativityFolder[] = [];
const EMPTY_IIO_FIELDS: RelativitySettingsField[] = [];

const SELECT_MODES = [
  { id: '1', name: 'Append Only' },
  { id: '2', name: 'Overlay Only' },
  { id: '3', name: 'Append/Overlay' },
];

const OVERLAY_IDENTIFIERS = [
  { id: 'Identifier', name: 'Identifier' },
  { id: 'BegBates', name: 'Beg Bates' },
  { id: 'RelativePath', name: 'Relative Path' },
];

const MULTI_SELECT_OPTIONS = [
  { id: '1', name: 'Use Relativity Defaults' },
  { id: '2', name: 'Merge All' },
  { id: '3', name: 'Replace All' },
];

const SUBFOLDER_TOKENS = [
  { id: 'RITM', name: 'Identifier 1' },
  { id: 'Evidence', name: 'Identifier 2' },
  { id: 'Release', name: 'Identifier 3' },
  { id: 'Folders', name: 'Folders' },
  { id: 'Custodian', name: 'Custodian' },
  { id: 'CollectionId', name: 'Collection ID' },
];

const RELATIVITY_FIELD_MAPPINGS: Record<string, string> = {
  Identifier: 'Identifier',
  BegBates: 'Begin Bates',
  EndBates: 'End Bates',
  FamilyBegAttachBates: 'Begin Bates Attachment',
  FamilyEndAttachBates: 'End Bates Attachment',
  FamilyBegAttachIdentifier: 'Group Identifier',
  AttachmentFilenameList: 'Attachments',
  NumberOfAttachments: 'Attachment Count',
  IIOIdentifier: 'UUID',
  Custodian: 'Custodian',
  MimeType: 'Media Type',
  AllCustodians: 'All Custodians',
  AllPaths: 'All Paths',
  OriginalFilename: 'File Name',
  OriginalFilenameExtension: 'File Extension',
  OriginalPath: 'Source Path',
  EmailPath: 'Email Folder Path',
  FileSize: 'File Size (Bytes)',
  Language: 'Primary La',
  MD5: 'MD5 Hash',
  SHA1: 'SHA1 Hash',
  ProcessingException: 'Exceptions',
  Creator: 'System Created By',
  Modifier: 'System Last Modified By',
  DocTitle: 'Title',
  EmailSubject: 'Email Subject',
  Author: 'File Author',
  OfficeCategory: 'Categories',
  HasTrackChanges: 'Track Changes',
  HasHiddenSheet: 'MS XL Hidden Sheets',
  HasHiddenRow: 'MS XL Hidden Rows',
  HasHiddenColumn: 'MS XL Hidden Columns',
  EmailId: 'Email Entry ID',
  EmailFrom: 'Email From',
  EmailTo: 'Email To',
  EmailCc: 'Email CC',
  EmailBcc: 'Email BCC',
  EmailAllRecipients: 'Recipients',
  EmailConversationIndex: 'Conversation Index',
  EmailHasDeliveryReceipt: 'Delivery Receipt Requested',
  EmailInternetMessageId: 'Email Message ID',
  EmailImportance: 'Importance',
  EmailSensitivity: 'Sensitivity',
  SentDateTime: 'Email Sent Date Time',
  ReceivedDateTime: 'Email Received Date Time',
  CreatedDateTime: 'File Created Date Time',
  ModifiedDateTime: 'File Modified Date Time',
  AccessedDateTime: 'Last Accessed Date Time',
  PrintDate: 'Last Printed Date Time',
  AppointmentStartDateTime: 'Email Start Date Time',
  AppointmentEndDateTime: 'Email End Date Time',
  TimezoneProcessed: 'Time Zone Field',
  EProperties: 'Document Type',
  Notes: 'MS PP Speaker Notes',
  EmailMessageClass: 'Email Message Class',
};

interface Props {
  settings: RelativitySettingsItem;
  onChange: (s: RelativitySettingsItem) => void;
  saving: boolean;
}

export function Step2_1RelativitySettings({ settings, onChange, saving }: Props) {
  const projectId = useProjectIdFromRoute();
  const isNew = !settings.id;

  const { data: workspaces = EMPTY_WORKSPACES } = useRelativityWorkspaces(projectId);
  const { data: workspaceFields = EMPTY_WORKSPACE_FIELDS } = useRelativityWorkspaceFields(projectId, settings.workspace?.id);
  const { data: workspaceFolders = EMPTY_WORKSPACE_FOLDERS } = useRelativityWorkspaceFolders(projectId, settings.workspace?.id);
  const { data: iioFields = EMPTY_IIO_FIELDS } = useRelativityIioFields(projectId);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const settingsRef = useRef(settings);
  settingsRef.current = settings;

  // Auto-map field mappings when a new settings' workspace is selected
  useEffect(() => {
    if (!isNew || !settings.workspace?.id || workspaceFields.length === 0 || iioFields.length === 0) return;

    const identifierRf = workspaceFields.find(f => f.isIdentifier);
    const mappedFields: RelativitySettingsField[] = iioFields.map((iioField, i) => {
      let outputName: string | null = null;
      let isEnabled = false;

      if (i === 0 && identifierRf) {
        outputName = identifierRf.name;
        isEnabled = true;
      } else {
        const rf = workspaceFields.find(f => RELATIVITY_FIELD_MAPPINGS[iioField.name] === f.name);
        if (rf) { outputName = rf.name; isEnabled = true; }
      }

      return { ...iioField, outputName, isEnabled };
    });

    onChangeRef.current({ ...settingsRef.current, fields: mappedFields });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settings.workspace?.id, workspaceFields, iioFields]);

  const set = <K extends keyof RelativitySettingsItem>(key: K, value: RelativitySettingsItem[K]) =>
    onChange({ ...settings, [key]: value });

  const setField = (index: number, updated: RelativitySettingsField) => {
    const fields = settings.fields.map((f, i) => (i === index ? updated : f));
    set('fields', fields);
  };

  const toggleAll = (enabled: boolean) => {
    set('fields', settings.fields.map(f => ({ ...f, isEnabled: enabled })));
  };

  const allEnabled = settings.fields.length > 0 && settings.fields.every(f => f.isEnabled);

  const rootFolderName = settings.rootFolder[0]?.name;
  const subFolderNames = settings.subFolder.map(f => f.name);
  const pathPreview = subFolderNames.length > 0
    ? (rootFolderName === '/' ? ' / ' : ` / ${rootFolderName} / `) + subFolderNames.join(' / ')
    : null;

  return (
    <div style={{ overflowX: 'hidden' }}>
      <FormGroup label="Settings name" htmlFor="rsName" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <input
          type="text"
          id="rsName"
          className="form-control"
          value={settings.name}
          maxLength={255}
          required
          disabled={saving}
          onChange={e => set('name', e.target.value)}
        />
      </FormGroup>

      <FormGroup label="Workspace" htmlFor="rsWorkspace" labelCols="col-sm-4 text-right" inputCols="col-sm-8">
        <MsComboBox
          id="rsWorkspace"
          selected={settings.workspace ? [settings.workspace] : []}
          onChange={items => {
            const ws = items.slice(-1)[0];
            onChange({ ...settings, workspace: ws, fields: [] });
          }}
          getKey={w => w.id}
          getLabel={w => w.name}
          items={workspaces}
          disabled={saving}
        />
      </FormGroup>

      <FormGroup
        label={<>Import Mode <TooltipIcon content={<ul style={{ margin: 0, paddingLeft: 16 }}><li><b>Append Only</b> - Will load documents as new records.</li><li><b>Overlay Only</b> - Updates existing records only.</li><li><b>Append/Overlay</b> - Will load new records and overlay data onto existing records.</li></ul>} /></>}
        htmlFor="rsSelectMode"
        labelCols="col-sm-4 text-right"
        inputCols="col-sm-8"
        labelAlign="middle"
      >
        <MsComboBox
          id="rsSelectMode"
          selected={settings.selectMode}
          onChange={items => set('selectMode', items.slice(-1))}
          getKey={m => m.id}
          getLabel={m => m.name}
          items={SELECT_MODES}
          disabled={saving}
        />
      </FormGroup>

      <FormGroup
        label={<>Overlay Identifier <TooltipIcon content="This field acts as a link indicating where to overlay the data." /></>}
        htmlFor="rsOverlayId"
        labelCols="col-sm-4 text-right"
        inputCols="col-sm-8"
        labelAlign="middle"
      >
        <MsComboBox
          id="rsOverlayId"
          selected={settings.overlayIdentifier}
          onChange={items => set('overlayIdentifier', items.slice(-1))}
          getKey={m => m.id}
          getLabel={m => m.name}
          items={OVERLAY_IDENTIFIERS}
          disabled={saving}
        />
      </FormGroup>

      <FormGroup
        label={<>Multi-Select <TooltipIcon content={<ul style={{ margin: 0, paddingLeft: 16 }}><li><b>Use Relativity Defaults</b> - Values are merged or replaced according to overlay behavior settings in the environment.</li><li><b>Merge Values</b> - Values are merged regardless of the overlay behavior settings in the environment.</li><li><b>Replace Values</b> — Replaces all values regardless of the overlay settings in the environment.</li></ul>} /></>}
        htmlFor="rsMultiSelect"
        labelCols="col-sm-4 text-right"
        inputCols="col-sm-8"
        labelAlign="middle"
      >
        <MsComboBox
          id="rsMultiSelect"
          selected={settings.multiSelect}
          onChange={items => set('multiSelect', items.slice(-1))}
          getKey={m => m.id}
          getLabel={m => m.name}
          items={MULTI_SELECT_OPTIONS}
          disabled={saving}
        />
      </FormGroup>

      {pathPreview && (
        <div className="form-group">
          <div className="col-sm-4" />
          <div className="col-sm-8">
            <span className="label label-default">{pathPreview}</span>
          </div>
        </div>
      )}

      <FormGroup
        label={<>Destination Folder <TooltipIcon content="The folder where new records will be loaded." /></>}
        htmlFor="rsRootFolder"
        labelCols="col-sm-4 text-right"
        inputCols="col-sm-8"
        labelAlign="middle"
      >
        <MsComboBox
          id="rsRootFolder"
          selected={settings.rootFolder}
          onChange={items => set('rootFolder', items.slice(-1))}
          getKey={f => f.id}
          getLabel={f => f.name}
          items={workspaceFolders}
          disabled={saving || !settings.workspace?.id}
        />
      </FormGroup>

      <FormGroup
        label={<>Sub Folder(s) <TooltipIcon content="Folder structure within root folder" /></>}
        htmlFor="rsSubFolder"
        labelCols="col-sm-4 text-right"
        inputCols="col-sm-8"
        labelAlign="middle"
      >
        <MsComboBox
          id="rsSubFolder"
          selected={settings.subFolder}
          onChange={items => set('subFolder', items)}
          getKey={t => t.id}
          getLabel={t => t.name}
          items={SUBFOLDER_TOKENS}
          disabled={saving}
        />
      </FormGroup>

      <div style={{ paddingBottom: 20 }} />

      <legend style={{ width: '100%' }}>Field Mappings</legend>

      <div className="table-responsive">
        <table className="table">
          <thead>
            <tr>
              <th style={{ minWidth: 20 }} />
              <th style={{ width: 350 }}>Relativity Field</th>
              <th style={{ width: 350 }}>Indexed I/O Field</th>
              <th style={{ minWidth: 100 }}>
                <ToggleSwitch
                  checked={allEnabled}
                  onChange={toggleAll}
                  onLabel="ON"
                  offLabel="OFF"
                  size="small"
                />
              </th>
            </tr>
          </thead>
          <DragDropProvider
            onDragEnd={({ canceled, operation }) => {
              if (canceled || !isSortable(operation.source)) return;
              const { initialIndex, index } = operation.source.sortable;
              if (initialIndex === index) return;
              const next = [...settings.fields];
              const [moved] = next.splice(initialIndex, 1);
              next.splice(index, 0, moved);
              set('fields', next);
            }}
          >
            <tbody>
              {settings.fields.map((field, i) => (
                <FieldMappingRow
                  key={field.name || `field-${i}`}
                  field={field}
                  index={i}
                  relativityFields={workspaceFields}
                  saving={saving}
                  onChange={updated => setField(i, updated)}
                />
              ))}
              {settings.fields.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-center text-muted">
                    {settings.workspace?.id ? 'Loading field mappings...' : 'Select a workspace to load field mappings.'}
                  </td>
                </tr>
              )}
            </tbody>
          </DragDropProvider>
        </table>
      </div>
    </div>
  );
}
